import React from 'react';
import { Link } from 'react-router-dom';

/**
 * Политика конфиденциальности QPrice.
 *
 * Черновик написан разработкой **по факту поведения системы**, а не по шаблону:
 * состав данных сверен с кодом бэкенда, с `PrivacyInfo.xcprivacy` и с реальным
 * порядком удаления аккаунта (`UserAccountDeletionService`,
 * `PartnerDeletionService`). Требование этапа 5.1 ТЗ: политика обязана
 * совпадать с анкетой App Privacy в App Store Connect и с манифестом
 * приложения — расхождение ревьюер ловит вручную.
 *
 * Поэтому правило простое: **меняется поведение — меняется этот текст**.
 * Особенно пункты 2 (состав данных), 4 (кому передаём) и 6 (удаление).
 *
 * Текст ждёт вычитки юристом заказчика. Фактическая часть верна, юридические
 * формулировки могут быть усилены.
 */
export const PrivacyContent: React.FC = () => (
  <>
    <p>
      Настоящая политика описывает, какие данные собирает Платформа «Qprice» — мобильное
      приложение (далее — «Приложение») и Личный кабинет Партнёра на сайте{' '}
      <code>qprice.kz</code> — зачем они нужны, кому передаются и как их удалить.
      Политика написана по факту работы Платформы: если поведение меняется, меняется и
      этот документ.
    </p>
    <p>
      Используя Приложение или Личный кабинет, вы соглашаетесь с этой политикой. Если не
      согласны — не используйте Платформу; аккаунт можно удалить в любой момент
      (раздел 6).
    </p>

    <h2>1. Кто обрабатывает данные</h2>
    <p>
      Оператор — Товарищество с ограниченной ответственностью «Big Brain Group»
      (БИН 211140024452), Республика Казахстан, г. Астана, — владелец и оператор
      Платформы «Qprice». Связь по любым вопросам о данных:{' '}
      <a href="mailto:partner@qprice.kz">partner@qprice.kz</a>,{' '}
      <a href="https://wa.me/77772468483" target="_blank" rel="noopener noreferrer">
        WhatsApp +7 777 246 84 83
      </a>
      . Отвечаем в течение рабочего дня.
    </p>
    <p>
      Обработка ведётся в соответствии с Законом Республики Казахстан «О персональных
      данных и их защите».
    </p>

    <h2>2. Какие данные мы собираем</h2>

    <h3>2.1. Пользователи Приложения</h3>
    <p>
      <strong>Номер телефона</strong> — это логин. На него приходит код подтверждения;
      пароля в сервисе нет.
    </p>
    <p>
      <strong>Имя</strong> — по желанию, показывается рядом с вашими отзывами.
      <strong> Электронная почта</strong> — необязательное поле профиля.
    </p>
    <p>
      <strong>Фотография профиля</strong> — если вы её загрузили. Показывается рядом с
      вашими отзывами.
    </p>
    <p>
      <strong>Город и интересы</strong> (категории заведений) — чтобы показывать
      предложения рядом и по теме.
    </p>
    <p>
      <strong>Отзывы</strong>: оценка, текст и до трёх фотографий. Отзывы публичны — их
      видят другие пользователи и заведение.
    </p>
    <p>
      <strong>Избранные заведения, история поиска, просмотренные карточки</strong> — чтобы
      выдача была полезнее и чтобы вы могли вернуться к найденному.
    </p>
    <p>
      <strong>Токен устройства</strong> — технический идентификатор, без которого
      невозможно доставить push-уведомление. Уведомления можно выключить в приложении
      целиком или по конкретному заведению.
    </p>
    <p>
      <strong>Геолокация</strong> — только если вы дали разрешение в системном диалоге.
      Используется, чтобы показать заведения поблизости. Отказ от геолокации не блокирует
      работу приложения — вы выбираете город вручную.
    </p>
    <p>
      <strong>Идентификатор устройства</strong> — до входа в аккаунт приложение хранит
      выбранные интересы на устройстве, чтобы лента была осмысленной у гостя. Идентификатор
      не связан с личностью и переносится в аккаунт при регистрации.
    </p>
    <p>
      <strong>Обезличенная статистика</strong>: факт открытия карточки заведения, акции или
      нажатия на контакты. Эти записи <em>не связаны с аккаунтом</em> — заведение видит
      только количество, а не то, кто именно смотрел.
    </p>

    <h3>2.2. Партнёры (заведения)</h3>
    <p>
      Партнёры — заведения общественного питания, услуг, торговли и иные субъекты
      предпринимательства. Номер телефона и имя владельца аккаунта; данные заведения —
      название, описание, адрес и координаты, категории, часы работы, контакты и ссылки
      на соцсети, фотографии, меню с ценами, скидки, истории и баннеры. Всё это публичная
      информация о заведении: она и показывается пользователям.
    </p>
    <p>
      Координаты устройства отправляются на сервер только когда партнёр сам нажимает
      «подставить текущую точку» при указании адреса заведения.
    </p>

    <h3>2.3. Чего мы не делаем</h3>
    <p>
      Мы <strong>не отслеживаем</strong> вас в других приложениях и на сайтах, не
      передаём данные рекламным сетям и брокерам данных, не продаём их. Рекламных
      идентификаторов Платформа не использует.
    </p>

    <h2>3. Зачем мы собираем данные</h2>
    <p>
      Только для работы Платформы: вход в аккаунт, показ предложений с учётом города и
      интересов, публикация отзывов и их модерация, уведомления о скидках, поддержка
      пользователей, статистика посещаемости для заведения и защита от злоупотреблений
      (спам, накрутка отзывов, повторная выдача пробного периода).
    </p>

    <h2>4. Кому передаются данные</h2>
    <p>
      <strong>Google (Firebase Cloud Messaging)</strong> — доставка push-уведомлений;
      передаётся токен устройства и текст уведомления.
    </p>
    <p>
      <strong>Green API / WhatsApp</strong> — доставка кода подтверждения при входе;
      передаётся номер телефона и текст сообщения с кодом.
    </p>
    <p>
      <strong>Хостинг-провайдер</strong> — серверы, на которых работает Платформа.
    </p>
    <p>
      Другим лицам данные не передаются, кроме случаев, когда этого требует закон
      (запрос уполномоченного органа в установленном порядке).
    </p>

    <h2>5. Где и сколько хранятся данные</h2>
    <p>
      Данные хранятся на серверах в защищённой инфраструктуре, доступ к ним есть только у
      сотрудников, которым он нужен для работы. Передача между Приложением и сервером
      идёт по HTTPS.
    </p>
    <p>
      Данные аккаунта хранятся, пока аккаунт существует. Обезличенная статистика посещений
      хранится в агрегированном виде и с личностью не связана. После удаления аккаунта —
      см. раздел 6.
    </p>

    <h2>6. Удаление аккаунта и данных</h2>
    <p>
      Аккаунт удаляется <strong>из самого Приложения</strong>: «Меню» → «Профиль» →
      «Удалить аккаунт». Запрос можно также направить на{' '}
      <a href="mailto:partner@qprice.kz">partner@qprice.kz</a> с номера телефона, на
      который зарегистрирован аккаунт.
    </p>
    <p>
      <strong>Удаление происходит сразу по запросу, отложенного срока нет.</strong> В момент
      обращения удаляются: имя, номер телефона, электронная почта, фотография профиля,
      город и интересы, избранное, история поиска и просмотров, токены устройств и все
      ваши отзывы вместе с прикреплёнными фотографиями. Отзывы именно удаляются, а не
      обезличиваются. Доступ по ранее выданным токенам прекращается немедленно.
    </p>
    <p>
      Для Партнёров дополнительно удаляются заведение, меню и позиции с ценами, скидки,
      истории, баннер и все загруженные фотографии, включая файлы на сервере.
    </p>
    <p>
      <strong>Что остаётся после удаления.</strong> Пустая учётная запись без персональных
      данных — она нужна для целостности базы. И одна запись в журнале выданных пробных
      периодов, в которой хранится <strong>номер телефона</strong>: только так можно не
      выдавать бесплатный период повторно на тот же номер. Это единственное исключение, и
      оно существует для предотвращения злоупотреблений.
    </p>
    <p>
      Повторная регистрация с того же номера создаёт новый пустой аккаунт — прежние данные
      в нём не восстанавливаются.
    </p>

    <h2>7. Ваши права</h2>
    <p>
      Вы вправе узнать, какие ваши данные мы обрабатываем, потребовать исправить неточные
      данные, удалить аккаунт и данные, отозвать согласие на обработку. Разрешения на
      геолокацию и уведомления отзываются в настройках устройства в любой момент.
    </p>
    <p>
      Для реализации прав напишите на <a href="mailto:partner@qprice.kz">partner@qprice.kz</a>{' '}
      с номера или адреса, указанных в аккаунте. Ответим в течение рабочего дня.
    </p>

    <h2>8. Пользовательский контент и модерация</h2>
    <p>
      Отзывы и фотографии публичны. Текст отзыва проходит автоматическую проверку при
      публикации, жалобы разбираются в течение 24 часов, автора можно заблокировать —
      подробнее в <Link to="/ugc-rules">Правилах публикации</Link>.
    </p>

    <h2>9. Дети</h2>
    <p>
      Платформа не предназначена для детей младше 13 лет и не собирает их данные
      осознанно.
      Если такие данные попали к нам, напишите — удалим.
    </p>

    <h2>10. Сайт и Личный кабинет Партнёра</h2>
    <p>
      Сайт использует только технические хранилища браузера, необходимые для входа в
      Личный кабинет (сессия и токен доступа). Рекламных и аналитических трекеров сторонних
      сетей на сайте нет.
    </p>

    <h2>11. Изменения политики</h2>
    <p>
      Мы можем обновлять политику — например, когда на Платформе появляются новые функции.
      Актуальная редакция всегда доступна по адресу <code>qprice.kz/privacy</code>, дата
      редакции указана вверху страницы. Существенные изменения мы сообщаем в Приложении.
    </p>

    <h2>12. Контакты</h2>
    <p>
      Вопросы о данных, запросы на удаление и жалобы:{' '}
      <a href="mailto:partner@qprice.kz">partner@qprice.kz</a>,{' '}
      <a href="https://wa.me/77772468483" target="_blank" rel="noopener noreferrer">
        WhatsApp +7 777 246 84 83
      </a>
      . Другие документы Платформы — на <Link to="/support">странице поддержки</Link>.
    </p>
  </>
);
